/**
 * The five questions behind the pre-signup diagnostic.
 *
 * WHY THIS EXISTS
 * 86 of 132 accounts have never logged a question. Of those who ever do,
 * 60% do it within an hour of signup and 83% within 24h, so a first
 * session that produces nothing almost never recovers. Logging requires
 * the student to already have practice materials open and transcribe from
 * them, which is far too much activation energy for cold social traffic.
 * This runs BEFORE signup and asks nothing but five taps.
 *
 * THE RULE THAT MATTERS
 * Maren already shipped a sample walkthrough (lib/sampleQuestions.ts). Of
 * the 12 users who completed it, ZERO went on to log a real question. The
 * mechanism is substitution: watching a demo built on someone else's data
 * teaches the student nothing about themselves. So here the questions are
 * provided, but the answers, the confidence and the stated reasons are
 * genuinely the user's own. Every field below defends that distinction.
 *
 * AAMC CONTENT RULE
 * These are ORIGINAL stems written to MCAT style and reasoning patterns.
 * None are copied from, paraphrased from, or generated from AAMC, UWorld,
 * Kaplan or Blueprint material. AAMC's terms prohibit derivative works and
 * explicitly name AI/LLM modelling, so never "improve" these by feeding
 * real exam items to a model.
 *
 * DIFFICULTY CURVE: warm-up, warm-up, hard, hard, medium. Each question is
 * engineered to fail in a DIFFERENT way, so two students who both score
 * 3/5 get genuinely different reads. A diagnostic where everyone fails
 * identically tells you nothing.
 *
 * Topic/subtopic values are canonical (backend/taxonomy.py). Free users
 * never hit the auto-classifier, so whatever ships here is stored verbatim
 * and shows up in weak-area surfaces as-is.
 */

import type { ErrorTag } from "./errorTags";
import { ERROR_TAGS } from "./errorTags";

export type DiagnosticSection = "CP" | "BB" | "PS";
export type Choice = "A" | "B" | "C" | "D";
/** Mirrors questions.confidence, an existing 1..5 integer column. */
export type ConfidenceWord = "guessing" | "fairly" | "sure";

export const CONFIDENCE_VALUE: Record<ConfidenceWord, number> = {
  guessing: 1,
  fairly: 3,
  sure: 5,
};

/** Two shapes, matching the app's existing card_type values. */
export type DiagnosticCard =
  | { format: "front_back"; front: string; back: string }
  | { format: "cloze"; cloze: string };

export interface DiagnosticQuestion {
  /** Stable identity. Persisted as question_number so a retake can replace
   *  rather than duplicate, and so a changed question set is detectable. */
  id: string;
  section: DiagnosticSection;
  topic: string;
  subtopic: string;
  difficulty: "warm-up" | "medium" | "hard";
  stem: string;
  choices: Record<Choice, string>;
  correct: Choice;
  explanation: string;
  /** One per question, fixed: the underlying idea. */
  concept: DiagnosticCard;
  /** Keyed by the reason the student gave. This is the differentiator:
   *  missing the buffer question because you blanked on the formula should
   *  produce a different card than missing it because you rushed.
   *  Every card is a RETRIEVAL item, never advice. "Sanity-check your
   *  work" is coaching; you cannot be quizzed on it. */
  habits: Partial<Record<ErrorTag, DiagnosticCard>>;
}

export const QUESTION_SET_VERSION = "v1";

export const DIAGNOSTIC_QUESTIONS: DiagnosticQuestion[] = [
  {
    id: "D1",
    section: "BB",
    topic: "Biochemistry",
    subtopic: "Metabolism & Bioenergetics",
    difficulty: "warm-up",
    stem:
      "A researcher treats isolated mitochondria with a compound that makes the inner mitochondrial membrane freely permeable to protons. ATP synthesis stops almost immediately, but oxygen consumption continues.\n\nWhich of the following best explains this observation?",
    choices: {
      A: "The compound directly inhibits the enzymes of the electron transport chain",
      B: "The proton gradient that drives ATP synthase has been dissipated",
      C: "ATP synthase has been denatured by the compound",
      D: "Oxygen can no longer serve as the final electron acceptor",
    },
    correct: "B",
    explanation:
      "This is an uncoupling agent. ATP synthase runs on the proton gradient, not on the electron transport chain directly. Once protons can leak back into the matrix the gradient collapses, so ATP synthesis stops. The chain itself is untouched and actually speeds up, since it is no longer working against a gradient. That is why oxygen consumption keeps going, and it is also what rules out A and D.",
    concept: {
      format: "front_back",
      front: "What directly powers ATP synthase?",
      back: "The proton gradient across the inner mitochondrial membrane, not the electron transport chain directly.",
    },
    habits: {
      conceptual_gap: {
        format: "front_back",
        front:
          "An uncoupler makes the inner mitochondrial membrane leaky to protons. What happens to ATP synthesis and to oxygen consumption?",
        back: "ATP synthesis stops. Oxygen consumption continues and often rises, because the chain no longer works against a gradient.",
      },
      process_of_elimination: {
        format: "front_back",
        front: "How do you tell an uncoupler from an ETC inhibitor in an experiment?",
        back: "Uncoupler: ATP falls but oxygen consumption continues or rises. ETC inhibitor: both fall together.",
      },
      misread: {
        format: "front_back",
        front: "In a mitochondrial experiment, what does continued oxygen consumption rule out?",
        back: "Direct inhibition of the chain, and loss of oxygen as the final electron acceptor. The chain has to still be turning over.",
      },
    },
  },
  {
    id: "D2",
    section: "PS",
    topic: "Psychology",
    subtopic: "Learning & Conditioning",
    difficulty: "warm-up",
    stem:
      "A student failed an important exam in a particular lecture hall. Weeks later, they notice they feel anxious every time they walk into that same hall, even for unrelated classes.\n\nIn classical conditioning terms, the lecture hall now functions as which of the following?",
    choices: {
      A: "Unconditioned stimulus",
      B: "Conditioned stimulus",
      C: "Unconditioned response",
      D: "Conditioned response",
    },
    correct: "B",
    explanation:
      "The hall started out neutral and caused no anxiety on its own. After being paired with the failure it now triggers anxiety by itself, which makes it a conditioned stimulus. The anxiety is the conditioned response, and that is what choice D describes. The failure itself was the unconditioned stimulus.",
    concept: {
      format: "cloze",
      cloze:
        "A once neutral cue that comes to trigger a learned reaction is the {{conditioned stimulus}}. The reaction it triggers is the {{conditioned response}}.",
    },
    habits: {
      careless: {
        format: "front_back",
        front: "In conditioning terms, which words name a stimulus and which name a reaction?",
        back: "A stimulus is something in the environment acting on the person. A response is what the person does. A place, object or sound is always a stimulus.",
      },
      conceptual_gap: {
        format: "front_back",
        front: "A student fails an exam in a room, then feels anxious in that room. Name the US, CS and CR.",
        back: "US is the failure experience. CS is the room. CR is the anxiety the room now triggers.",
      },
      misread: {
        format: "front_back",
        front: 'A question asks what a cue "functions as." Are you naming a stimulus or a response?',
        back: "A stimulus. The cue acts on the person. The feeling it produces is the response.",
      },
    },
  },
  {
    id: "D3",
    section: "CP",
    topic: "Physics",
    subtopic: "Work, Energy & Power",
    difficulty: "hard",
    stem:
      "A 2.0 kg block is released from rest and slides down a frictionless incline from a height of 5.0 m. At the bottom it slides along a frictionless horizontal surface and compresses a spring with spring constant 400 N/m.\n\nWhat is the maximum compression of the spring? (Use g = 10 m/s²)",
    choices: { A: "0.50 m", B: "0.71 m", C: "1.0 m", D: "1.4 m" },
    correct: "B",
    explanation:
      "All of the gravitational potential energy becomes spring potential energy at maximum compression.\n\nmgh = ½kx²\n(2.0)(10)(5.0) = ½(400)x²\n100 = 200x², so x² = 0.5 and x = 0.71 m\n\nIf you picked 0.50 m you dropped the ½ from the spring term. That is the most common slip on this question, and it is a precision problem rather than a physics problem.",
    concept: {
      format: "front_back",
      front: "A mass falls height h onto a spring of constant k. What is the maximum compression?",
      back: "mgh = ½kx², so x = √(2mgh/k).",
    },
    habits: {
      formula_recall: {
        format: "front_back",
        front: "Which energy expressions carry a factor of ½?",
        back: "Kinetic (½mv²) and spring (½kx²). Gravitational potential energy (mgh) does not.",
      },
      careless: {
        format: "front_back",
        front: "Before you substitute numbers into an energy problem, what should already be on the page?",
        back: "The complete symbolic equation with both ½ factors written in. mgh = ½kx².",
      },
      timing: {
        format: "front_back",
        front: "Does the incline angle change the speed at the bottom of a frictionless slope?",
        back: "No. Energy conservation makes the path irrelevant. Only the height matters.",
      },
    },
  },
  {
    id: "D4",
    section: "BB",
    topic: "Biochemistry",
    subtopic: "Enzyme Structure & Function",
    difficulty: "hard",
    stem:
      "An enzyme is studied in the presence and absence of compound Y. In the presence of Y, both the measured Vmax and the measured Km decrease.\n\nCompound Y is best classified as which of the following?",
    choices: {
      A: "Competitive inhibitor",
      B: "Noncompetitive inhibitor",
      C: "Uncompetitive inhibitor",
      D: "Allosteric activator",
    },
    correct: "C",
    explanation:
      "An uncompetitive inhibitor binds only the enzyme-substrate complex, never free enzyme. Binding to ES pulls it out of the equilibrium, which drives the reaction toward forming more ES. That is what lowers the apparent Km. The enzyme's actual affinity for substrate has not changed at all, only the measured value. Vmax still falls because inhibited complexes cannot turn over.\n\nCompetitive raises apparent Km and leaves Vmax alone. Classic noncompetitive lowers Vmax and leaves Km alone. An activator would not lower Vmax at all. The trap is that a lower Km reads like better binding, which sounds like help rather than inhibition.",
    concept: {
      format: "front_back",
      front: "An inhibitor lowers both Vmax and apparent Km. Which type is it?",
      back: "Uncompetitive. It binds only the enzyme-substrate complex.",
    },
    habits: {
      process_of_elimination: {
        format: "front_back",
        front: "How do competitive, noncompetitive and uncompetitive inhibition each affect Vmax and apparent Km?",
        back: "Competitive: Km up, Vmax unchanged. Noncompetitive: Vmax down, Km unchanged. Uncompetitive: both down.",
      },
      trap_answer: {
        format: "front_back",
        front: "Why does an uncompetitive inhibitor lower the apparent Km?",
        back: "Binding ES removes it from the equilibrium, which pulls the reaction toward more ES. Only the apparent Km falls. Real affinity is unchanged.",
      },
      conceptual_gap: {
        format: "front_back",
        front: "What does an uncompetitive inhibitor bind to?",
        back: "Only the enzyme-substrate complex. Never free enzyme.",
      },
    },
  },
  {
    id: "D5",
    section: "CP",
    topic: "General Chemistry",
    subtopic: "Acids & Bases",
    difficulty: "medium",
    stem:
      "A buffer solution contains 0.10 M of a weak acid HA and 0.010 M of its conjugate base A⁻. The pKa of HA is 4.8.\n\nWhat is the approximate pH of this solution?",
    choices: { A: "2.8", B: "3.8", C: "4.8", D: "5.8" },
    correct: "B",
    explanation:
      "pH = pKa + log([A⁻]/[HA]) = 4.8 + log(0.010/0.10) = 4.8 + log(0.1) = 4.8 − 1.0 = 3.8\n\nThe habit worth stealing here: there is ten times more acid than base, so the pH has to land below the pKa. Work out the direction before you touch the arithmetic. Picking 5.8 means the ratio got flipped. Same math, wrong way.",
    concept: {
      format: "cloze",
      cloze: "pH = pK{{a}} + log( {{[A⁻]}} / [HA] ). The conjugate base goes on top.",
    },
    habits: {
      trap_answer: {
        format: "front_back",
        front: "Before calculating a buffer's pH, how do you predict whether it lands above or below the pKa?",
        back: "Compare [A⁻] to [HA]. Ratio above 1 puts the pH above the pKa. Ratio below 1 puts it below.",
      },
      formula_recall: {
        format: "front_back",
        front: "In Henderson-Hasselbalch, which species goes on top of the log ratio?",
        back: "The conjugate base, [A⁻]. pH = pKa + log([A⁻]/[HA]).",
      },
      careless: {
        format: "front_back",
        front: "What is log(0.1)?",
        back: "Negative 1. A tenfold excess of acid puts the pH exactly one unit below the pKa.",
      },
    },
  },
];

/** Generic retrieval cards, used when a reason has no question-specific
 *  card. Every reason must produce SOMETHING or the promise breaks. */
export const FALLBACK_HABIT_CARDS: Record<ErrorTag, DiagnosticCard> = {
  conceptual_gap: {
    format: "front_back",
    front: "When you hit a concept you don't know, what should go in your log?",
    back: 'The exact sub-idea you were missing, not the topic name. "Uncouplers vs ETC inhibitors" beats "metabolism."',
  },
  misread: {
    format: "front_back",
    front: "What is the last check before you select an answer?",
    back: "Re-read the final sentence of the stem. Make sure you are answering the question asked and not the one you expected.",
  },
  careless: {
    format: "front_back",
    front: "After solving, what one step catches most careless errors?",
    back: "Check that the units and the order of magnitude are plausible before you select.",
  },
  timing: {
    format: "front_back",
    front: "Fifteen seconds left on a question. What is the best move?",
    back: "Cut the two choices you are most sure are wrong, then commit. Never leave it blank.",
  },
  formula_recall: {
    format: "front_back",
    front: "What goes on the page before you substitute any numbers?",
    back: "The full symbolic equation with every coefficient already written in.",
  },
  process_of_elimination: {
    format: "front_back",
    front: "You are down to two choices. What now?",
    back: "Find the one word that differs between them, then go back to the stem and test that word.",
  },
  trap_answer: {
    format: "front_back",
    front: "What makes a distractor tempting on the MCAT?",
    back: "It answers a slightly different question. Usually a flipped ratio, a reversed direction, or a skipped step.",
  },
};

/** One student's answer to one diagnostic question. */
export interface DiagnosticAnswer {
  id: string;
  picked: Choice;
  confidence: ConfidenceWord;
  /** Capped at 2 by the UI. Empty when the answer was confidently correct. */
  reasons: ErrorTag[];
  timeSpentSec?: number;
}

/**
 * Shape accepted by the backend write path. Field names mirror the
 * existing QuestionLog contract exactly so the same validation applies.
 *
 * result_type uses "Weak Correct" for a correct-but-guessed answer. That
 * value ALREADY exists in the product, so a lucky guess maps onto real
 * vocabulary instead of being mislabelled a miss. Contaminating "Missed"
 * would break its meaning across every other surface.
 *
 * why_wrong is REQUIRED by the backend whenever result_type is Missed or
 * Weak Correct, so it is derived from the reasons the student actually
 * selected rather than invented.
 */
export interface DiagnosticLogPayload {
  question_text: string;
  section: DiagnosticSection;
  exam_bank_name: string;
  source: string;
  question_number: string;
  topic: string;
  subtopic: string;
  result_type: "Correct" | "Missed" | "Weak Correct";
  why_wrong: string;
  correct_concept: string;
  confidence: number;
  error_tags: ErrorTag[];
  user_answer: Choice;
  correct_answer: Choice;
  time_spent_sec?: number;
}

export const DIAGNOSTIC_BANK_NAME = "Maren Diagnostic";

export function questionById(id: string): DiagnosticQuestion | undefined {
  return DIAGNOSTIC_QUESTIONS.find((q) => q.id === id);
}

/** True when the student did not actually know it: wrong, or right by guess. */
export function isShaky(a: DiagnosticAnswer): boolean {
  const q = questionById(a.id);
  if (!q) return false;
  return a.picked !== q.correct || a.confidence === "guessing";
}

export function resultTypeFor(a: DiagnosticAnswer): DiagnosticLogPayload["result_type"] {
  const q = questionById(a.id);
  if (!q) return "Correct";
  if (a.picked !== q.correct) return "Missed";
  return a.confidence === "guessing" ? "Weak Correct" : "Correct";
}

/** The cards this answer earned: one per reason given, plus the concept.
 *  Cloze cards sort to the top of any stack. */
export function cardsFor(a: DiagnosticAnswer): { kind: "habit" | "concept"; card: DiagnosticCard }[] {
  const q = questionById(a.id);
  if (!q) return [];
  const out: { kind: "habit" | "concept"; card: DiagnosticCard }[] = a.reasons.map((r) => ({
    kind: "habit" as const,
    card: q.habits[r] ?? FALLBACK_HABIT_CARDS[r],
  }));
  out.push({ kind: "concept", card: q.concept });
  return out.sort(
    (x, y) => Number(y.card.format === "cloze") - Number(x.card.format === "cloze"),
  );
}

/** Converts one answer into the backend contract. */
export function toLogPayload(a: DiagnosticAnswer): DiagnosticLogPayload | null {
  const q = questionById(a.id);
  if (!q) return null;
  const result_type = resultTypeFor(a);
  // Required by the backend for Missed / Weak Correct. Built from the
  // student's own stated reasons so it is their words, not ours.
  const why_wrong =
    result_type === "Correct"
      ? ""
      : a.reasons.length
        ? a.reasons.map((r) => ERROR_TAGS[r]).join("; ")
        : "Marked as not confidently known during the onboarding diagnostic.";

  return {
    question_text: q.stem,
    section: q.section,
    exam_bank_name: DIAGNOSTIC_BANK_NAME,
    source: DIAGNOSTIC_BANK_NAME,
    question_number: q.id,
    topic: q.topic,
    subtopic: q.subtopic,
    result_type,
    why_wrong,
    correct_concept: "",
    confidence: CONFIDENCE_VALUE[a.confidence],
    error_tags: a.reasons,
    user_answer: a.picked,
    correct_answer: q.correct,
    time_spent_sec: a.timeSpentSec,
  };
}
